import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo } from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import AdditionalSavings from './AdditionalSavings';
import ComparePaymentOptionsCta from './ComparePaymentOptionsCta';
import Error from '../PriceComponent/Error';
import Loader from '../../utils/components/Loader';
import PAYMENT_CALCULATOR_COMPONENT_TYPE from './constants';
import PaymentAnnualKmAllowance from './PaymentAnnualKmAllowance';
import PaymentFaqLink from './PaymentFaqLink';
import PaymentFrequencyTabs from './PaymentFrequencyTabs';
import PaymentMethodOptions from './PaymentMethodOptions';
import PriceInfo from './PriceInfo';
import TermAndRateDropdown from './TermAndRateDropdown';
import TradeInOptions from './TradeInOptions';
import ViewOffers from './ViewOffers';
import gtmBapDispatch from '../../core/gtm/gtmBapDispatch';
import useIncentiveOffers from './ViewOffers/useIncentiveOffers';
import useScreenSize from '../../utils/hooks/useScreenSize';
import { offerSelectionChanged, paymentMethodChanged, selectIncentiveOffers, selectIsPaymentMethodCash, selectIsPaymentMethodLease, setAdditionalLeaseAnnualKmAllowance, setIncludeFees, setLeaseAnnualKmAllowance, setOverriddenIsSellingProvince, setPaymentFrequency, setTermAndRate, } from '../../core/reducers/financialCalculators';
import { offerPrimaryChanged } from '../../core/reducers/offers';
import { useAppDispatch, useAppSelector } from '../../core/utils';
import { useFetchCurrentVehicleFinancialCalculatorPayment } from '../../core/apiHooks/useFetchPayment';
import useBapRegisterUserInteraction from '../../utils/hooks/bap/useBapRegisterUserInteraction';
const PaymentCalculator = ({ id, showPriceBreakdown, isCompareSection, onPaymentOptionsChanged, hasAccordionPadding, }) => {
    const { t } = useTranslation();
    const { isSmall } = useScreenSize();
    const dispatch = useAppDispatch();
    const { registerUserInteraction } = useBapRegisterUserInteraction('paymentCalculator');
    const { isFetching: isPaymentDataFetching, isLoading: isPaymentDataLoading, isUninitialized: isPaymentDataUninitialized, data: termWithRateData, error, } = useFetchCurrentVehicleFinancialCalculatorPayment(id);
    const { loyaltyOffers, stackableOffers, nonStackableOffers, provincialGovernmentIncentives, federalGovernmentIncentives, additionalSavingsOffers, currentVehicleTransmissionKey: transmissionKey, paymentMethod, } = useIncentiveOffers(id);
    const selectedOfferIds = useAppSelector(state => selectIncentiveOffers(state, id));
    const isPaymentMethodCash = useAppSelector(state => selectIsPaymentMethodCash(state, id));
    const isPaymentMethodLease = useAppSelector(state => selectIsPaymentMethodLease(state, id));
    const handlePaymentOptionsChanged = useCallback(() => {
        if (onPaymentOptionsChanged) {
            onPaymentOptionsChanged();
        }
    }, [onPaymentOptionsChanged]);
    const debouncedOfferSelectionDispatch = useMemo(() => debounce(params => {
        const { calculatorId, offerId, isSelected, _transmissionKey, _paymentMethod } = params;
        dispatch(offerSelectionChanged({ calculatorId, offerId, isSelected }));
        dispatch(offerPrimaryChanged({
            calculatorId,
            offerId,
            transmissionKey: _transmissionKey,
            paymentMethod: _paymentMethod,
            isSelected,
        }));
        gtmBapDispatch({
            calculatorId,
            event: 'bap_payment_option',
            termWithRateData,
            additionalSavingsOffers,
        });
        handlePaymentOptionsChanged();
    }, 300), [dispatch, handlePaymentOptionsChanged, termWithRateData, additionalSavingsOffers]);
    // Then in your component:
    const debounceOfferSelectiondHandler = useCallback((offerId, isSelected) => {
        debouncedOfferSelectionDispatch({
            calculatorId: id,
            offerId,
            isSelected,
            _transmissionKey: transmissionKey,
            _paymentMethod: paymentMethod,
        });
    }, [id, transmissionKey, paymentMethod, debouncedOfferSelectionDispatch]);
    const handleOfferSelectionChanged = useCallback((offerId, isSelected) => {
        registerUserInteraction();
        debounceOfferSelectiondHandler(offerId, isSelected);
    }, [debounceOfferSelectiondHandler]);
    if (isPaymentDataUninitialized || isPaymentDataLoading) {
        return _jsx(Loader, { testId: "cy-payment-calculator-loading-state" });
    }
    if (error) {
        return (_jsx(Error, { message: t('Pages.Bap.apiLoadingError'), verticalAlignment: "center", errorIconName: "exclamationAlert" }));
    }
    const handlePaymentMethodChanged = (selectedPaymentMethod) => {
        registerUserInteraction();
        dispatch(paymentMethodChanged(id, selectedPaymentMethod));
        handlePaymentOptionsChanged();
        gtmBapDispatch({
            calculatorId: id,
            event: 'bap_payment_option',
            termWithRateData,
            additionalSavingsOffers,
        });
    };
    const handlePaymentFrequencyChanged = (paymentFrequency) => {
        registerUserInteraction();
        dispatch(setPaymentFrequency({ id, paymentFrequency }));
        handlePaymentOptionsChanged();
        gtmBapDispatch({
            calculatorId: id,
            event: 'bap_payment_option',
            termWithRateData,
            additionalSavingsOffers,
        });
    };
    const handleAnnualKmChanged = (annualKm) => {
        registerUserInteraction();
        dispatch(setLeaseAnnualKmAllowance({ id, leaseAnnualKmAllowance: annualKm }));
        handlePaymentOptionsChanged();
    };
    const handleAdditionalAnnualKmChanged = (additionalKm) => {
        registerUserInteraction();
        dispatch(setAdditionalLeaseAnnualKmAllowance({ id, additionalLeaseAnnualKmAllowance: additionalKm }));
        handlePaymentOptionsChanged();
    };
    const handleTermChanged = (term) => {
        registerUserInteraction();
        dispatch(setTermAndRate({ id, termAndRate: term }));
        handlePaymentOptionsChanged();
    };
    const handleIncludeFeesChanged = (includeFees) => {
        dispatch(setIncludeFees({ id, includeFees }));
        handlePaymentOptionsChanged();
    };
    const handleOverriddenIsSellingProvinceChanged = (overriddenIsSellingProvince) => {
        dispatch(setOverriddenIsSellingProvince({ id, overriddenIsSellingProvince }));
        handlePaymentOptionsChanged();
    };
    const handleTradeInOptionsFieldChanged = () => {
        handlePaymentOptionsChanged();
    };
    const handleGtmComparePayment = () => {
        gtmBapDispatch({
            calculatorId: id,
            event: 'bap_payment_compare',
            termWithRateData,
            additionalSavingsOffers,
        });
    };
    return (_jsxs(Box, { "component-type": PAYMENT_CALCULATOR_COMPONENT_TYPE, "data-testid": "cy-payment-calculator", pt: hasAccordionPadding && ['m', 'm', 'l'], children: [_jsx(Optional, { when: isCompareSection, children: _jsx(PriceInfo, { calculatorId: id, disableAllFields: isPaymentDataFetching, showPriceBreakdown: showPriceBreakdown, onIncludeFeesChange: handleIncludeFeesChanged, onOverriddenIsSellingProvinceChange: handleOverriddenIsSellingProvinceChanged, isCompareSection: isCompareSection }) }), _jsx(PaymentMethodOptions, { calculatorId: id, disableAllFields: isPaymentDataFetching, onChange: handlePaymentMethodChanged }), _jsx(PaymentFaqLink, {}), _jsx(ViewOffers, { calculatorId: id, disableAllFields: isPaymentDataFetching, loyaltyOffers: loyaltyOffers, stackableOffers: stackableOffers, nonStackableOffers: nonStackableOffers, provincialGovernmentIncentives: provincialGovernmentIncentives, federalGovernmentIncentives: federalGovernmentIncentives, handlePaymentOptionsChanged: handlePaymentOptionsChanged }), _jsxs(Optional, { when: !isCompareSection, children: [_jsx(PriceInfo, { calculatorId: id, disableAllFields: isPaymentDataFetching, showPriceBreakdown: showPriceBreakdown, onIncludeFeesChange: handleIncludeFeesChanged, onOverriddenIsSellingProvinceChange: handleOverriddenIsSellingProvinceChanged, componentType: PAYMENT_CALCULATOR_COMPONENT_TYPE }), _jsx(Optional, { when: !isSmall, children: _jsx(ComparePaymentOptionsCta, { handleGtmComparePayment: handleGtmComparePayment }) })] }), _jsxs(Optional, { when: !isPaymentMethodCash, children: [_jsx(PaymentFrequencyTabs, { calculatorId: id, disableAllFields: isPaymentDataFetching, onChange: handlePaymentFrequencyChanged }), _jsx(TermAndRateDropdown, { calculatorId: id, disableAllFields: isPaymentDataFetching, onChange: handleTermChanged })] }), _jsx(Optional, { when: isPaymentMethodLease, children: _jsx(PaymentAnnualKmAllowance, { calculatorId: id, disableAllFields: isPaymentDataFetching, onAnnualKmChanged: handleAnnualKmChanged, onAdditionalKmChanged: handleAdditionalAnnualKmChanged }) }), _jsx(TradeInOptions, { calculatorId: id, disableAllFields: isPaymentDataFetching, onFieldChange: handleTradeInOptionsFieldChanged }), _jsx(Optional, { when: additionalSavingsOffers && additionalSavingsOffers.length, children: _jsx(AdditionalSavings, { disableAllFields: isPaymentDataFetching, incentiveOffers: additionalSavingsOffers, selectedOfferIds: selectedOfferIds, onOfferSelected: handleOfferSelectionChanged }) })] }));
};
export default PaymentCalculator;
